// Formatering för ledningsrapporten – delas av PDF-mallen (servern) och
// fliken i ELvis Hub (klienten). Måste vara webbläsarsäker: inga node-importer.
//
// Svensk typografi: negativa tal och förändringar skrivs med "−" (U+2212),
// aldrig bindestreck; intervall med "–" (U+2013).

import type { LedningsrapportUnderlag } from './typer';

export const MINUS = String.fromCharCode(0x2212); // −
export const TANKSTRECK = String.fromCharCode(0x2013); // –

/** Not om "Nya luckor" – samma ordalydelse i PDF:en och fliken. */
export const NYA_LUCKOR_NOT =
  'Nya luckor omfattar helt obesvarade, delvis besvarade och tekniska fel – vid ett tekniskt fel kunde ELvis inte svara alls, oavsett kunskapsbasen.';

const MANADER = [
  'januari', 'februari', 'mars', 'april', 'maj', 'juni',
  'juli', 'augusti', 'september', 'oktober', 'november', 'december',
];

const talformat = new Intl.NumberFormat('sv-SE');

/** Tal i sv-SE-format ("1 642"), minustecken alltid U+2212. */
export function talSv(n: number): string {
  return talformat.format(n).replace(/[-−]/g, MINUS);
}

/** Singular för exakt 1 (och −1), annars plural – även 0: "0 källor", "1 källa". */
export function plural(n: number, en: string, flera: string): string {
  return Math.abs(n) === 1 ? en : flera;
}

/** Tal + rätt form: antalSv(1, 'fråga', 'frågor') → "1 fråga". */
export function antalSv(n: number, en: string, flera: string): string {
  return `${talSv(n)} ${plural(n, en, flera)}`;
}

export type Riktning = 'upp' | 'ner' | 'lika' | 'ny' | 'saknas';

/**
 * Förändring mot föregående period.
 * `fore === null` → saknas (ingen jämförelse finns). Från 0 till mer → "ny".
 * Annars procent avrundad till heltal: "+12 %", "−8 %", "±0 %".
 */
export function forandring(nu: number, fore: number | null): { text: string; riktning: Riktning } {
  if (fore === null) return { text: TANKSTRECK, riktning: 'saknas' };
  if (fore === 0) {
    if (nu > 0) return { text: 'ny', riktning: 'ny' };
    return { text: '±0 %', riktning: 'lika' };
  }
  const procent = Math.round(((nu - fore) / fore) * 100);
  if (procent === 0) return { text: '±0 %', riktning: 'lika' };
  if (procent > 0) return { text: `+${talSv(procent)} %`, riktning: 'upp' };
  return { text: `${MINUS}${talSv(-procent)} %`, riktning: 'ner' };
}

function delar(iso: string): { ar: number; manad: number; dag: number } {
  const [ar, manad, dag] = iso.slice(0, 10).split('-').map(Number);
  return { ar, manad, dag };
}

/** "2026-10-01" → "oktober 2026". */
export function manadsnamn(manad: string): string {
  const { ar, manad: m } = delar(manad);
  return `${MANADER[m - 1]} ${ar}`;
}

/** "2026-10-01" → "oktober". */
export function manadKort(manad: string): string {
  return MANADER[delar(manad).manad - 1];
}

/** "2026-10-09" → "9 oktober". */
export function datumSv(datum: string): string {
  const { manad, dag } = delar(datum);
  return `${dag} ${MANADER[manad - 1]}`;
}

/** Föregående månad, "2026-10-01" → "september". */
export function foregaendeManad(manad: string): string {
  const { manad: m } = delar(manad);
  return MANADER[(m + 10) % 12];
}

/** Tidpunkt i Stockholmstid: "1 november 2026 kl. 05.00". */
export function tidpunktSv(iso: string): string {
  const d = new Date(iso);
  const p = Object.fromEntries(
    new Intl.DateTimeFormat('sv-SE', {
      timeZone: 'Europe/Stockholm',
      year: 'numeric', month: 'numeric', day: 'numeric',
      hour: '2-digit', minute: '2-digit', hour12: false,
    })
      .formatToParts(d)
      .map((x) => [x.type, x.value]),
  );
  return `${Number(p.day)} ${MANADER[Number(p.month) - 1]} ${p.year} kl. ${p.hour}.${p.minute}`;
}

export type Amnesrad = {
  amne_id: number;
  namn: string;
  antal: number;
  totalt: number;
  forandring: { text: string; riktning: Riktning };
};

/**
 * Ämnena i månaden med förändring och totalt, flest frågor först.
 *
 * När ELvis inte loggade föregående månad (`elvis_foregaende === null`) är
 * ämnenas föregående-siffror nollor utan betydelse – raderna får då ingen
 * förändring ('saknas'), aldrig "ny".
 */
export function amnenIManaden(u: LedningsrapportUnderlag): Amnesrad[] {
  const jamforbar = u.anvandning.elvis_foregaende !== null;
  const totalt = new Map(u.amnen_totalt.map((a) => [a.amne_id, a.antal]));
  return u.amnen_manad
    .map((a, i) => ({
      i,
      rad: {
        amne_id: a.amne_id,
        namn: a.namn,
        antal: a.antal,
        totalt: totalt.get(a.amne_id) ?? 0,
        forandring: forandring(a.antal, jamforbar ? a.foregaende : null),
      },
    }))
    .sort((x, y) => y.rad.antal - x.rad.antal || y.rad.totalt - x.rad.totalt || x.i - y.i)
    .map((x) => x.rad);
}

/** Luckor per ämne med förändring; ämnen utan luckor båda månaderna utelämnas. */
export function luckorPerAmne(u: LedningsrapportUnderlag): Omit<Amnesrad, 'totalt'>[] {
  return u.luckor.per_amne
    .filter((a) => a.antal > 0 || a.foregaende > 0)
    .map((a, i) => ({ i, a }))
    .sort((x, y) => y.a.antal - x.a.antal || x.i - y.i)
    .map(({ a }) => ({
      amne_id: a.amne_id,
      namn: a.namn,
      antal: a.antal,
      forandring: forandring(a.antal, a.foregaende),
    }));
}
